import { graphqlApi } from '@/graphql'
import { baseApi } from '@/services'
import { Expense, MultipleExpenses, PaginatedResults } from '@/types/unlock'

export const SubscriberApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getExpensesByApplication: builder.query<
      PaginatedResults<Expense[]>,
      {
        applicationUUID: string
        page?: number
        pageSize?: number
        state?: string
        type?: string
      }
    >({
      query: (args) => {
        const { applicationUUID, state, type, page = 1, pageSize = 50 } = args
        return {
          url: `v2/applications/${applicationUUID}/expenses/`,
          method: 'GET',
          params: {
            page: page,
            page_size: pageSize,
            state: state,
            type: type,
          },
        }
      },
      providesTags: ['SubscriberV2'],
    }),
    createExpense: builder.mutation<
      Expense,
      {
        applicationUUID: string
        body: Partial<Expense>
      }
    >({
      query: (args) => {
        const { applicationUUID, body } = args
        return {
          method: 'POST',
          url: `v2/applications/${applicationUUID}/expenses/`,
          body,
        }
      },
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'SubscriberV2',
        'Alert',
      ],
    }),
    updateExpense: builder.mutation<
      Expense,
      {
        applicationUUID: string
        expense: Partial<Expense>
      }
    >({
      query: ({ applicationUUID, expense }) => {
        return {
          method: 'PATCH',
          url: `v2/applications/${applicationUUID}/expenses/${expense.uuid}/`,
          body: expense,
        }
      },
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'SubscriberV2',
        'Alert',
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(graphqlApi.util.invalidateTags(['gql:applicationCalcs']))
      },
    }),
    deleteExpense: builder.mutation<
      Expense,
      {
        applicationUUID: string
        expenseUUID: string
      }
    >({
      query: (args) => {
        const { applicationUUID, expenseUUID } = args
        return {
          method: 'DELETE',
          url: `v2/applications/${applicationUUID}/expenses/${expenseUUID}/`,
        }
      },
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'SubscriberV2',
        'Alert',
      ],
    }),
    createMultipleExpenses: builder.mutation<
      Expense[],
      {
        applicationUUID: string
        body: Partial<MultipleExpenses>
      }
    >({
      query: (args) => {
        const { applicationUUID, body } = args
        return {
          method: 'POST',
          url: `v2/applications/${applicationUUID}/expenses/multiple-expenses/`,
          body,
        }
      },
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'SubscriberV2',
        'Alert',
      ],
    }),
  }),
})

export const {
  useGetExpensesByApplicationQuery,
  useLazyGetExpensesByApplicationQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useCreateMultipleExpensesMutation,
} = SubscriberApi
